import { ArrowLeft } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { Link, useLocation, useParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'
import { type AccidentViolationItem, useAccidentsViolation } from '@/models/dashboard'
import { ClaimDetailCards } from './ClaimDetailCards'
import { SafetyEventDetail } from './SafetyEventDetail'
import { isClaim } from './violationDisplay'

function BackLink({ page }: { page?: number }) {
  return (
    <Link to="/insure/violations" state={page ? { page } : undefined}>
      <Button variant="ghost" size="sm" className="gap-2">
        <ArrowLeft className="size-4" />
        Back to violations
      </Button>
    </Link>
  )
}

function ItemDetail({ item, page }: { item: AccidentViolationItem; page?: number }) {
  return (
    <>
      <Helmet>
        <title>{item.title}</title>
      </Helmet>

      <div className="flex flex-col gap-6">
        <div>
          <div className="mb-4">
            <BackLink page={page} />
          </div>
          <h1 className="text-2xl font-bold capitalize">{item.title}</h1>
          <p className="text-muted-foreground mt-1">{item.description}</p>
        </div>

        {isClaim(item) ? <ClaimDetailCards item={item} /> : <SafetyEventDetail item={item} />}
      </div>
    </>
  )
}

export function ViolationDetail() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navState = location.state as { item?: AccidentViolationItem; page?: number } | null
  const stateItem = navState?.item
  const page = navState?.page

  // A refresh or deep link carries no navigation state, and the row may be on any page.
  const { item: fetchedItem, isLoading, loadFailed } = useAccidentsViolation(id, { enabled: !stateItem })

  const item = stateItem ?? fetchedItem

  if (isLoading && !stateItem) {
    return (
      <SkeletonGroup className="flex flex-col gap-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </SkeletonGroup>
    )
  }

  if (!item) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink page={page} />
        <p className="text-muted-foreground">
          {loadFailed ? "We couldn't load this claim. Try again in a moment." : 'Not found.'}
        </p>
      </div>
    )
  }

  return <ItemDetail item={item} page={page} />
}
